import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import KeywordBox from "../../components/KeywordBox";
import AudioService, { AUDIO_CATEGORIES, AUDIO_SOURCE_LABELS } from "./AudioService";
import { ADMIN_AUDIO_NEW_PATH, adminAudioEditPath } from "./audioRoutes";
import { downloadAudioAsset } from "./audioUtils";
import { getColumns } from "./getColumns";

const ADMIN_AUDIO_SEARCH_KEY = "admin.audio.searchOptions";
const VALID_CATEGORIES = new Set(["", ...AUDIO_CATEGORIES]);
const VALID_SOURCES = new Set(["", ...AUDIO_SOURCE_LABELS]);

function loadSearchOptions() {
    try {
        const raw = localStorage.getItem(ADMIN_AUDIO_SEARCH_KEY);
        if (!raw) {
            return { nameKeyword: "", categoryFilter: "", sourceFilter: "" };
        }
        const parsed = JSON.parse(raw);
        const nameKeyword = typeof parsed?.nameKeyword === "string" ? parsed.nameKeyword : "";
        const categoryFilter =
            typeof parsed?.categoryFilter === "string" && VALID_CATEGORIES.has(parsed.categoryFilter)
                ? parsed.categoryFilter
                : "";
        const sourceFilter =
            typeof parsed?.sourceFilter === "string" && VALID_SOURCES.has(parsed.sourceFilter)
                ? parsed.sourceFilter
                : "";
        return { nameKeyword, categoryFilter, sourceFilter };
    } catch {
        return { nameKeyword: "", categoryFilter: "", sourceFilter: "" };
    }
}

function saveSearchOptions(options) {
    try {
        localStorage.setItem(ADMIN_AUDIO_SEARCH_KEY, JSON.stringify(options));
    } catch {
        /* ignore quota / private mode */
    }
}

export default function AdminAudioCloudTab() {
    const navigate = useNavigate();
    const location = useLocation();
    const initialSearch = useMemo(() => loadSearchOptions(), []);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState(initialSearch.categoryFilter);
    const [sourceFilter, setSourceFilter] = useState(initialSearch.sourceFilter || "");
    const [nameKeyword, setNameKeyword] = useState(initialSearch.nameKeyword);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [totalRows, setTotalRows] = useState(0);
    const [sortModel, setSortModel] = useState([{ field: "createdAt", sort: "desc" }]);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [rowToDelete, setRowToDelete] = useState(null);

    const audioRef = useRef(null);
    const [playingId, setPlayingId] = useState(null);
    const [loadingPlayId, setLoadingPlayId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);

    const fetchList = async () => {
        setLoading(true);
        const sort =
            sortModel.length > 0 && sortModel[0].field && sortModel[0].sort
                ? `${sortModel[0].field}:${sortModel[0].sort}`
                : "createdAt:desc";
        const result = await AudioService.list(
            paginationModel.page,
            paginationModel.pageSize,
            sort,
            categoryFilter || null,
            nameKeyword || null,
            sourceFilter || null
        );
        setLoading(false);
        if (result.success) {
            setRows(result.data.content || []);
            setTotalRows(result.data.totalElements ?? 0);
        } else {
            setSeverity("error");
            setMessage(result.error || "Failed to load audio");
            setOpenSnackbar(true);
        }
    };

    useEffect(() => {
        fetchList();
    }, [paginationModel, sortModel, categoryFilter, nameKeyword, sourceFilter]);

    useEffect(() => {
        saveSearchOptions({ nameKeyword, categoryFilter, sourceFilter });
    }, [nameKeyword, categoryFilter, sourceFilter]);

    useEffect(() => {
        const msg = location.state?.message;
        if (msg) {
            setSeverity("success");
            setMessage(msg);
            setOpenSnackbar(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state?.message, location.pathname, navigate]);

    const stopPlayback = useCallback(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.pause();
            audio.removeAttribute("src");
        }
        setPlayingId(null);
        setLoadingPlayId(null);
    }, []);

    useEffect(() => stopPlayback, [stopPlayback]);

    const handleTogglePlay = useCallback(
        async (row) => {
            if (!row?.url) return;
            if (playingId === row.id) {
                stopPlayback();
                return;
            }
            if (!audioRef.current) {
                audioRef.current = new Audio();
                audioRef.current.preload = "auto";
            }
            const audio = audioRef.current;
            audio.pause();
            audio.onended = () => {
                setPlayingId(null);
                setLoadingPlayId(null);
            };
            audio.src = row.url;
            audio.currentTime = 0;
            setLoadingPlayId(row.id);
            try {
                await audio.play();
                setPlayingId(row.id);
            } catch (e) {
                setPlayingId(null);
                setSeverity("error");
                setMessage(e?.message || "Failed to play audio");
                setOpenSnackbar(true);
            } finally {
                setLoadingPlayId(null);
            }
        },
        [playingId, stopPlayback],
    );

    const handleToggleEnable = async (row, checked) => {
        const result = await AudioService.update(row.id, { enable: checked, includeEnable: true });
        if (result.success) {
            setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, enable: checked } : r)));
        } else {
            setSeverity("error");
            setMessage(result.error || "Failed to update enable");
            setOpenSnackbar(true);
        }
    };

    const handleDeleteClick = (row) => {
        setRowToDelete(row);
        setOpenDeleteDialog(true);
    };

    const handleDownloadClick = useCallback(async (row) => {
        if (!row?.url || !row?.id) return;
        setDownloadingId(row.id);
        try {
            await downloadAudioAsset(row);
        } catch (e) {
            setSeverity("error");
            setMessage(e?.message || "Failed to download audio");
            setOpenSnackbar(true);
        } finally {
            setDownloadingId(null);
        }
    }, []);

    const handleConfirmDelete = async () => {
        const id = rowToDelete?.id;
        if (!id) return;
        if (playingId === id) {
            stopPlayback();
        }
        const result = await AudioService.delete(id);
        if (result.success) {
            setRowToDelete(null);
            setSeverity("success");
            setMessage(`Deleted "${rowToDelete.name || "audio"}"`);
            setOpenSnackbar(true);
            fetchList();
        } else {
            setSeverity("error");
            setMessage(result.error || "Failed to delete audio");
            setOpenSnackbar(true);
        }
    };

    const columns = getColumns({
        onEditClick: (row) => navigate(adminAudioEditPath(row.id)),
        onToggleEnable: handleToggleEnable,
        onTogglePlay: handleTogglePlay,
        onDownloadClick: handleDownloadClick,
        onDeleteClick: handleDeleteClick,
        playingId,
        loadingPlayId,
        downloadingId,
    });

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 2, gap: 2, flexWrap: "wrap" }}>
                <KeywordBox
                    initialValue={initialSearch.nameKeyword}
                    callback={(value) => {
                        setNameKeyword(value);
                        setPaginationModel((p) => ({ ...p, page: 0 }));
                    }}
                    placeholder="Search by name"
                    size="small"
                    sx={{ minWidth: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="audio-category-filter-label">Category</InputLabel>
                    <Select
                        labelId="audio-category-filter-label"
                        label="Category"
                        value={categoryFilter}
                        onChange={(e) => {
                            setCategoryFilter(e.target.value);
                            setPaginationModel((p) => ({ ...p, page: 0 }));
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {AUDIO_CATEGORIES.map((c) => (
                            <MenuItem key={c} value={c}>
                                {c}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="audio-source-filter-label">Source</InputLabel>
                    <Select
                        labelId="audio-source-filter-label"
                        label="Source"
                        value={sourceFilter || ""}
                        onChange={(e) => {
                            setSourceFilter(e.target.value);
                            setPaginationModel((p) => ({ ...p, page: 0 }));
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {AUDIO_SOURCE_LABELS.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(ADMIN_AUDIO_NEW_PATH)}>
                    Upload audio
                </Button>
            </Box>

            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.id}
                loading={loading}
                paginationMode="server"
                sortingMode="server"
                rowCount={totalRows}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                pageSizeOptions={[10, 20, 50]}
                disableRowSelectionOnClick
                onRowClick={(params) => navigate(adminAudioEditPath(params.row.id))}
                sx={{
                    minHeight: 400,
                    bgcolor: "background.paper",
                    "& .MuiDataGrid-cell[data-field='actions'], & .MuiDataGrid-cell[data-field='enable']": {
                        display: "flex",
                        alignItems: "center",
                    },
                }}
            />

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
            <ConfirmDialog
                open={openDeleteDialog}
                setOpen={setOpenDeleteDialog}
                title="Delete audio"
                targetName={rowToDelete?.name || ""}
                message="Delete this audio file from the cloud library? This cannot be undone."
                callback={handleConfirmDelete}
            />
        </Box>
    );
}
